import React, {useEffect, useRef, useState} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLoading, usePersistedTransactionId } from '../../context';
import {
  HeroLogo,
  Footer,
  Button,
  Label,
  TextInput,
  FormItemContainer,
  FormContainer, SingleLineFormItemWrapper,
} from '../../components';
import AliPayPlusPaymentImage from '../../resource/figma-images/alipay-plus-payment-image.png';
import ContinueToPayImage from '../../resource/figma-images/continue-to-pay-image.svg';
import { Alert, Stack, styled } from '@mui/material';
import ReCAPTCHA from "react-google-recaptcha";
import {
  getDisplaySurchargeRate,
  getPageHeroLogo,
  getPageTitle,
  getReferencePlaceholder,
  getThemeColor,
  shouldVerifyRecaptcha
} from "../../customHostConfigOnClient";
import {
  PaymentContent,
  PaymentDetails, PaymentHiddenWrapper,
  PaymentOption,
  PaymentOptionImage,
  PaymentOptionList,
  PaymentOptionText,
  SurchargeAmountWrapper
} from "../../components/PaymentMethod";
import {useRecaptchaReady, verifyRecaptcha} from "../../utils/useRecaptchaReady";
import {PrivacyPolicy} from "../../components/PrivacyPolicy";


const InfoMessage = styled('div')`
  color: darkgray;
  text-align: center;
`;

const PaymentFormPageTitle = styled('div')`
  font-size: 15px;
  text-align: center;
  font-weight: 500;
  color: #000;
`;


const INITIAL_PAYMENT_METHOD = 'initial payment method';

export const LexusSalesPaymentForm = () => {
  const [billAmount, setBillAmount] = useState('');
  const [billReference, setBillReference] = useState('');
  const [billAmountDisabled, setBillAmountDisabled] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [error, setError] = useState('');
  const [paymentResult, setPaymentResult] = useState('');
  const [billAmountError, setBillAmountError] = useState(false);
  const [billReferenceError, setBillReferenceError] = useState(false);
  const recaptchaRef = useRef(null);
  const recaptchaReady = useRecaptchaReady();
  const { showLoading, hideLoading } = useLoading();
  const persistedTransactionId = usePersistedTransactionId();

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('alipay');

  // Hide /lexus-sales page for other domains.
  if (window.location.hostname !== 'lexusblackburn-sales.oligo.com.au' && window.location.hostname !== 'localhost') {
    window.location = '/' + window.location.search;
  }

  useEffect(() => {
    if (paymentResult !== '') setSelectedPaymentMethod('initial payment method');
  }, [paymentResult]);

  const handlePaymentMethodChange =
    (paymentMethod) => () => {
      setSelectedPaymentMethod(prev => (prev !== paymentMethod ? paymentMethod : prev));
    };

  const envParam = new URLSearchParams(useLocation().search).get('env');

  const handleBillAmountChange = (value) => {
    if (isValidFloat(value)) {
      setBillAmount(value);
      setBillAmountError(false);
    }
  };

  const handleBillReferenceChange = (value) => {
    setBillReference(value);
    setBillReferenceError(false);
  };

  const isValidFloat = (value) => {
    return /^-?\d*\.?\d*$/.test(value);
  };

  const createPaymentOrderAndNavigateForAlipay = async (data) => {
    window.location.href = data.redirect_url;
  };

  const getPaymentEndpoint = (selectedPayment) => {
    switch (selectedPayment) {
      case 'wechatpay':
        return '/api/wechat/create-payment';
      case 'alipay':
        return '/api/alipay/create-payment';
      case 'alipay-dana':
        return '/api/alipay-dana/create-payment';
      default:
        return '';
    }
  }

  const adjustCreatePaymentEndpoint = (endpoint) => {
    if (window.location.hostname === 'www.vidatennis.com.au') return `https://vida.oligo.com.au${endpoint}`;
    return endpoint;
  }

  const createPaymentOrderAndNavigate = async () => {
    if (!billAmount) {
      setBillAmountError(true);
      return;
    }

    if (!billReference) {
      setBillReferenceError(true);
      return;
    }

    setBillAmountDisabled(true);

    if (selectedPaymentMethod === 'initial payment method') {
      setError('Please select a payment method.');
      return;
    }

    if (shouldVerifyRecaptcha() && recaptchaReady && recaptchaRef.current) {
        // Validate ReCAPTCHA
      await verifyRecaptcha(recaptchaRef, setError);
    }

    try {
      showLoading();

      const createPaymentEndpoint = getPaymentEndpoint(selectedPaymentMethod);
      const createPaymentEndpointWithAdjustedHost = adjustCreatePaymentEndpoint(createPaymentEndpoint);
      const response = await fetch(`${createPaymentEndpointWithAdjustedHost}?env=${envParam}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          billAmount,
          billReference,
          userEmail,
          persistedTransactionId,
        }),
      });
      const data = await response.json();
      if (data.return_code === 'SUCCESS') {
        if (selectedPaymentMethod === 'alipay' || selectedPaymentMethod === 'alipay-dana') {
          // hideLoading('Payment submit successful!', 'success');
          await createPaymentOrderAndNavigateForAlipay(data);
        }
      } else {
        hideLoading('Payment creation failed.', 'error');
      }
    } catch (err) {
      hideLoading();
      setError('Create payment order failed, please try again.');
    }
  };

  const handlePayBill = async () => {
    try {
      await createPaymentOrderAndNavigate();
    } catch (err) {
      setError('Payment validation request sending failed');
      console.error(err);
    }
  };

  return (
    <FormContainer>
      <HeroLogo logo={getPageHeroLogo()} redirectPath='/lexus-sales' />
      <Stack gap="20px" alignItems="center">
        <PaymentFormPageTitle>{ getPageTitle() }</PaymentFormPageTitle>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack gap="20px" width="100%">
          <SingleLineFormItemWrapper>
            <FormItemContainer marginRight={'10px'}>
              <Label isRequired>Payment Amount</Label>
              <TextInput
                placeholder="$"
                value={billAmount || ''}
                onChange={handleBillAmountChange}
                error={billAmountError}
                disabled={billAmountDisabled}
              />
            </FormItemContainer>
            <FormItemContainer>
              <Label isRequired>Reference</Label>
              <TextInput
                placeholder={getReferencePlaceholder()}
                value={billReference || ''}
                onChange={handleBillReferenceChange}
                error={billReferenceError}
                disabled={billAmountDisabled}
              />
            </FormItemContainer>
          </SingleLineFormItemWrapper>
          <FormItemContainer>
            <Label>Receipt Email</Label>
            <TextInput
              placeholder="Enter your email if you would like a receipt of payment confirmation"
              value={userEmail || ''}
              onChange={setUserEmail}
            />
          </FormItemContainer>

          <div>
            <Label>Payment Method</Label>

            <PaymentOptionList>
              <PaymentOption
                onClick={handlePaymentMethodChange('alipay')}
                selected={selectedPaymentMethod === 'alipay'}
                borderColor={getThemeColor()}
              >
                <PaymentOptionImage src={AliPayPlusPaymentImage} />
                <PaymentOptionText>Alipay</PaymentOptionText>
              </PaymentOption>
            </PaymentOptionList>

            { selectedPaymentMethod !== INITIAL_PAYMENT_METHOD && getDisplaySurchargeRate(selectedPaymentMethod) &&
              <SurchargeAmountWrapper>Alipay Surcharge of {getDisplaySurchargeRate(selectedPaymentMethod)}. Daily transfer limit of $60,000.</SurchargeAmountWrapper>
            }

            <PaymentDetails>
              <PaymentHiddenWrapper show={selectedPaymentMethod === 'alipay'}>
                <PaymentContent>
                  <img src={ContinueToPayImage} />
                  <div>Select ‘Continue to pay’ and you will be redirected to Alipay to complete payment.</div>
                </PaymentContent>
              </PaymentHiddenWrapper>
            </PaymentDetails>

            { paymentResult && <InfoMessage>{paymentResult}</InfoMessage> }

            { shouldVerifyRecaptcha() && <ReCAPTCHA
              ref={recaptchaRef}
              sitekey="6LcWNAUrAAAAAHX2haltRqZqMAMZ0qB29zv16EB8"
              size="invisible"
            /> }

            <Button $themeColor={getThemeColor()} onClick={handlePayBill} block>
              Continue to pay
            </Button>
          </div>

        </Stack>
      </Stack>
      <PrivacyPolicy />
      <Footer />
    </FormContainer>
  );
};
